import { useEffect, useRef, useState, useCallback } from 'react';
import { EDGE_FUNCTION_URL, type Device, type Position, type Event } from '@/lib/traccar-api';

const WS_URL = EDGE_FUNCTION_URL ? EDGE_FUNCTION_URL.replace('https://', 'wss://') : ''

interface TraccarMessage {
  devices?: Device[];
  positions?: Position[];
  events?: Event[];
}

interface UseTraccarWebSocketReturn {
  positions: Position[];
  devices: Device[];
  events: Event[];
  isConnected: boolean;
  error: string | null;
  connect: () => void;
  disconnect: () => void;
}

export const useTraccarWebSocket = (sessionToken?: string | null): UseTraccarWebSocketReturn => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionRef = useRef(sessionToken);
  
  // Keep session ref updated
  sessionRef.current = sessionToken;

  const connect = useCallback(() => {
    const token = sessionRef.current;

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    const wsUrl = `${WS_URL}/api/socket?session=${token}`;
    console.log('Connecting to Traccar WebSocket...');

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      setError(null);
    };

    ws.onmessage = (event) => {
      try {
        const data: TraccarMessage = JSON.parse(event.data);

        // Handle connection confirmation
        if ('type' in data && (data as any).type === 'connected') {
          console.log('Traccar connection confirmed');
          return;
        }

        // Update positions - merge with existing using Map for O(1) lookups
        if (data.positions && data.positions.length > 0) {
          setPositions(prev => {
            const posMap = new Map(prev.map(p => [p.deviceId, p]));
            data.positions!.forEach(newPos => posMap.set(newPos.deviceId, newPos));
            return Array.from(posMap.values());
          });
        }

        // Update devices - merge with existing using Map for O(1) lookups
        if (data.devices && data.devices.length > 0) {
          setDevices(prev => {
            const devMap = new Map(prev.map(d => [d.id, d]));
            data.devices!.forEach(newDev => devMap.set(newDev.id, newDev));
            return Array.from(devMap.values());
          });
        }

        // Add new events
        if (data.events && data.events.length > 0) {
          setEvents(prev => [...data.events!, ...prev].slice(0, 100));
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
      setError('WebSocket connection error');
    };

    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      setIsConnected(false);
      wsRef.current = null;

      // Auto-reconnect after 5 seconds if not intentionally closed
      if (event.code !== 1000) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect...');
          connect();
        }, 5000);
      }
    };
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    
    setIsConnected(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    positions,
    devices,
    events,
    isConnected,
    error,
    connect,
    disconnect,
  };
};
